import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { AuthFacade } from '@core/services/auth-facade';
import { Responsive } from '@core/services/responsive';
import { Theme } from '@core/services/theme';
import { environment } from '@environments/environment';
import { NotificationDataModel } from '@models/notification-data.model';

interface NavItem {
  label: string;
  link: string;
}

interface AccountItem {
  label: string;
  iconName: string;
  tab: string;
}

interface HeaderNotification extends NotificationDataModel {
  readonly route: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-header',
  imports: [
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    MatMenuModule,
    MatDividerModule,
    NgTemplateOutlet,
    NgOptimizedImage,
  ],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header {
  private readonly dialog = inject(MatDialog);
  private readonly responsive = inject(Responsive);
  private readonly theme = inject(Theme);
  readonly authFacade = inject(AuthFacade);
  router = inject(Router);
  readonly signInRoute = environment.name === 'production' ? '/early-access' : '/auth/login';
  readonly signUpRoute = environment.name === 'production' ? '/early-access' : '/auth/signup';

  navItems = signal<NavItem[]>([
    // { label: 'Dashboard', link: '/dashboard' },
    { label: 'Projects', link: '/projects' },
    { label: 'Documents', link: '/documents' },
    // { label: 'Team', link: '/team' },
    // { label: 'Pricing', link: '/pricing' },
  ]);

  homeItems = signal<NavItem[]>([
    { label: 'Product', link: '#product' },
    { label: 'How it works', link: '#how-it-works' },
    { label: 'Use cases', link: '#use-cases' },
  ]);

  accountItems = signal<AccountItem[]>([
    { label: 'Profile', iconName: 'account_circle', tab: 'profile' },
    { label: 'Notifications', iconName: 'notifications', tab: 'notifications' },
    { label: 'Billing', iconName: 'credit_card', tab: 'billing' },
    { label: 'Security', iconName: 'shield', tab: 'security' },
  ]);

  readonly notifications: readonly HeaderNotification[] = [];

  showMenu = this.responsive.below('lg');

  currentTheme = this.theme.mode;

  isHomeAndUnauthenticated = computed(() => {
    const url = this.router.url;
    const path = url.split('#')[0].split('?')[0];

    return path === '/' && !this.authFacade.isAuthenticated();
  });

  cycleTheme() {
    this.theme.cycle();
  }

  logout() {
    void this.authFacade.logout();
  }

  openNotification(notification: HeaderNotification): void {
    console.log(notification);
  }

  async openCreateProjectDialog() {
    const { CreateDialog } = await import('@features/projects/dialogs/create-dialog/create-dialog');

    this.dialog.open(CreateDialog, {
      width: '1280px',
    });
  }
}
